import React, { useCallback, useEffect, useRef, useState } from "react";
import { getPhotoUrl } from "../../../../../common/utils/getPhotoUrl";
import { getUserInfoByUuids } from "../../../apis/chatApi";
import { get as getCachedUser, set as setCachedUser } from "../../../stores/userDisplayUtils";
import { getMessengerOpenRoomId, getMessengerVisible } from "../../../stores/messengerOpenRoomStore";
import icons from "../../../../../assets/images/icons";
import styles from "./ChatNotificationToast.module.scss";

const AUTO_DISMISS_MS = 5000;
const MAX_TOAST = 3;

function formatBody(message) {
  const text = message.text || "";
  const types = Array.isArray(message.messageType) ? message.messageType : [];
  const attachments = message.attachments || [];

  const hasFile = types.includes("FILE");
  const hasText = types.includes("TEXT");
  const hasContent = types.includes("CONTENT");

  const imageCount = attachments.filter((a) =>
    (a.contentType || "").startsWith("image/")
  ).length;
  const fileCount = attachments.length - imageCount;

  const parts = [];
  if (hasText && text) parts.push(text);
  if (hasFile && (imageCount > 0 || fileCount > 0)) {
    const fileParts = [];
    if (imageCount > 0) fileParts.push(imageCount === 1 ? "(이미지)" : `(이미지 ${imageCount})`);
    if (fileCount > 0) fileParts.push(fileCount === 1 ? "(파일)" : `(파일 ${fileCount})`);
    parts.push(fileParts.join(", "));
  }
  if (hasContent) {
    if (message?.content?.contentType === "sale") {
      parts.unshift("(매물)");
    } else {
      parts.unshift("(컨텐츠)");
    }

  }

  return parts.join(" ") || text || "새 메시지가 도착했습니다";
}

let nextId = 0;

function ChatNotificationToast() {
  // toasts: 개별 알림 (3개 이하)
  const [toasts, setToasts] = useState([]);
  const [dismissingIds, setDismissingIds] = useState(new Set());
  // badgeCount: 4개 이상일 때 배지 모드
  const [badgeCount, setBadgeCount] = useState(0);
  const [badgeDismissing, setBadgeDismissing] = useState(false);
  const timersRef = useRef({});
  const badgeTimerRef = useRef(null);
  // 총 누적 알림 수 (배지 전환 판단용)
  const totalCountRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    clearTimeout(badgeTimerRef.current);
  }, []);

  const dismissOne = useCallback((id) => {
    setDismissingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setToasts((prev) => prev.filter((n) => n.id !== id));
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      delete timersRef.current[id];
    }, 200);
  }, []);

  const reset = useCallback(() => {
    clearAllTimers();
    setToasts([]);
    setDismissingIds(new Set());
    setBadgeCount(0);
    setBadgeDismissing(false);
    totalCountRef.current = 0;
  }, [clearAllTimers]);

  const handleToastClick = useCallback((noti) => {
    reset();
    const isMobile = window.innerWidth <= 768;
    window.renderWidgetByName?.("Chat", "react-Chat", {
      isMobile,
      roomId: noti.roomId,
    });
  }, [reset]);

  const handleBadgeClick = useCallback(() => {
    reset();
    const isMobile = window.innerWidth <= 768;
    window.renderWidgetByName?.("Chat", "react-Chat", { isMobile });
  }, [reset]);

  const handleClose = useCallback((e, id) => {
    e.stopPropagation();
    clearTimeout(timersRef.current[id]);
    dismissOne(id);
    totalCountRef.current = Math.max(0, totalCountRef.current - 1);
  }, [dismissOne]);

  const dismissBadge = useCallback(() => {
    setBadgeDismissing(true);
    setTimeout(() => {
      setBadgeCount(0);
      setBadgeDismissing(false);
      totalCountRef.current = 0;
    }, 200);
  }, []);

  useEffect(() => {
    const onMessage = (e) => {
      const { message } = e.detail || {};
      if (!message?.messageId) return;

      const openRoomId = getMessengerOpenRoomId();
      // 해당 채팅방을 보고 있으면 스킵 (방 안)
      if (openRoomId && message.roomId === openRoomId) return;
      // 모바일 채팅 목록에서도 해당 방 알림 스킵 (목록에서 프리뷰로 보이므로)
      const isMobile = window.innerWidth <= 768;
      if (isMobile && getMessengerVisible() && !openRoomId) return;

      const myId = window.loggedInUser?.uuid;
      if (message.senderId === myId) return;

      const cached = getCachedUser(message.senderId);
      const id = ++nextId;
      totalCountRef.current += 1;

      const noti = {
        id,
        roomId: message.roomId,
        senderId: message.senderId,
        senderName: cached?.name || message.senderId,
        senderPhoto: cached?.photo || null,
        body: formatBody(message),
      };

      // 캐시 미스 시 API로 유저 정보 가져와서 토스트 업데이트
      if (!cached) {
        getUserInfoByUuids([message.senderId]).then((users) => {
          const u = users?.[0];
          if (!u?.uuid) return;
          const resolved = { name: u.name || null, photo: getPhotoUrl(u.photo, "agent") };
          setCachedUser(u.uuid, resolved);
          setToasts((prev) =>
            prev.map((t) =>
              t.id === id ? { ...t, senderName: resolved.name || t.senderName, senderPhoto: resolved.photo } : t
            )
          );
        }).catch(() => {});
      }

      // 이미 배지 모드이면 카운트만 업데이트
      if (totalCountRef.current > MAX_TOAST) {
        // 토스트가 남아있으면 제거
        setToasts((prev) => {
          prev.forEach((t) => {
            clearTimeout(timersRef.current[t.id]);
            delete timersRef.current[t.id];
          });
          return [];
        });
        setBadgeCount(totalCountRef.current);
        // 배지 자동 소멸 타이머 리셋
        clearTimeout(badgeTimerRef.current);
        badgeTimerRef.current = setTimeout(dismissBadge, AUTO_DISMISS_MS);
        return;
      }

      // 4개째 도달 → 배지 모드로 전환
      if (totalCountRef.current === MAX_TOAST + 1) {
        setToasts((prev) => {
          prev.forEach((t) => {
            clearTimeout(timersRef.current[t.id]);
            delete timersRef.current[t.id];
          });
          return [];
        });
        setBadgeCount(totalCountRef.current);
        clearTimeout(badgeTimerRef.current);
        badgeTimerRef.current = setTimeout(dismissBadge, AUTO_DISMISS_MS);
        return;
      }

      // 3개 이하: 토스트 스택
      setToasts((prev) => [noti, ...prev]);
      timersRef.current[id] = setTimeout(() => {
        dismissOne(id);
        totalCountRef.current = Math.max(0, totalCountRef.current - 1);
      }, AUTO_DISMISS_MS);
    };

    window.addEventListener("messageReceived", onMessage);
    return () => {
      window.removeEventListener("messageReceived", onMessage);
      clearAllTimers();
    };
  }, [dismissOne, dismissBadge, clearAllTimers]);

  // 채팅방 열리면 모든 알림 제거
  useEffect(() => {
    const onChatOpen = () => reset();
    window.addEventListener("chatWidgetOpened", onChatOpen);
    return () => window.removeEventListener("chatWidgetOpened", onChatOpen);
  }, [reset]);

  const hasToasts = toasts.length > 0;
  const hasBadge = badgeCount > 0;

  if (!hasToasts && !hasBadge) return null;

  return (
    <div className={styles.overlay}>
      {hasToasts && (
        <div className={styles.stack}>
          {toasts.map((noti) => (
            <div
              key={noti.id}
              className={`${styles.toast} ${dismissingIds.has(noti.id) ? styles.slideOut : ""}`}
              onClick={() => handleToastClick(noti)}
            >
              <div className={styles.container}>
                <div className={styles.content}>
                  <div className={`${styles.avatar} ${!noti.senderPhoto ? styles.avatarDefault : ""}`}>
                    {noti.senderPhoto ? (
                      <img src={noti.senderPhoto} alt="" />
                    ) : (
                      "👤"
                    )}
                  </div>
                  <div className={styles.message}>
                    <p className={styles.senderName}>{noti.senderName}</p>
                    <p className={styles.body}>{noti.body}</p>
                  </div>
                </div>
                <button
                  className={styles.closeBtn}
                  onClick={(e) => handleClose(e, noti.id)}
                  aria-label="닫기"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {hasBadge && (
        <div
          className={`${styles.badge} ${badgeDismissing ? styles.slideOut : ""}`}
          onClick={handleBadgeClick}
        >
          <div className={styles.badgeBg} />
          <div className={styles.badgeContent}>
            <div className={styles.badgeIcon}>
              <img src={icons.messager.chatAlarmToastBell} alt="" width="22" height="22" />
              <span className={styles.badgeDot} />
            </div>
            <span className={styles.badgeText}>
              읽지않은 알림이 {badgeCount}개 있습니다
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatNotificationToast;
